import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'

export const HISTORY_TAB = {
  details: 'details',
  chat: 'chat',
  activity: 'activity',
  loadboard: 'loadboard',
} as const

export type HistoryTab = (typeof HISTORY_TAB)[keyof typeof HISTORY_TAB]

const DETAILS_TAB = {
  value: HISTORY_TAB.details,
  labelKey: 'detailSidebar.tabs.details',
} as const

const CHAT_TAB = {
  value: HISTORY_TAB.chat,
  labelKey: 'detailSidebar.tabs.chat',
} as const

const ACTIVITY_TAB = {
  value: HISTORY_TAB.activity,
  labelKey: 'detailSidebar.tabs.activity',
} as const

const LOADBOARD_TAB = {
  value: HISTORY_TAB.loadboard,
  labelKey: 'detailSidebar.tabs.loadboard',
} as const

interface ShipmentHistoryTabsProps {
  value?: HistoryTab
  onValueChange?: (value: HistoryTab) => void
  activityCount?: number
  chatCount?: number
  showLoadboard?: boolean
  loadboardPosted?: boolean
  className?: string
}

interface IndicatorStyle {
  left: number
  width: number
}

function CountBadge({ count }: { count: number }) {
  return (
    <span className="flex h-3.5 items-center rounded-full bg-gray-100 px-1.5 py-px">
      <span className="text-center text-2xs-plus font-medium text-gray-500">
        {count}
      </span>
    </span>
  )
}

function HistoryTabButton({
  label,
  active,
  count,
  showDot,
  onClick,
  innerRef,
}: {
  label: string
  active: boolean
  count?: number
  showDot?: boolean
  onClick: () => void
  innerRef: React.RefCallback<HTMLButtonElement>
}) {
  return (
    <button
      ref={innerRef}
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className="flex h-full select-none items-center justify-center gap-1.5 px-3"
    >
      {showDot && (
        <span
          aria-hidden="true"
          className="size-2 shrink-0 rounded-full bg-green-500"
        />
      )}
      <span
        className={cn(
          'text-xs leading-none transition-colors',
          active ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900',
        )}
      >
        {label}
      </span>
      {count != null && count > 0 && <CountBadge count={count} />}
    </button>
  )
}

function tabCount(
  tab: HistoryTab,
  activityCount?: number,
  chatCount?: number,
): number | undefined {
  if (tab === HISTORY_TAB.activity) return activityCount
  if (tab === HISTORY_TAB.chat) return chatCount
  return undefined
}

export function ShipmentHistoryTabs({
  value,
  onValueChange,
  activityCount,
  chatCount,
  showLoadboard = false,
  loadboardPosted = false,
  className,
}: ShipmentHistoryTabsProps) {
  const tabs = showLoadboard
    ? [DETAILS_TAB, CHAT_TAB, LOADBOARD_TAB, ACTIVITY_TAB]
    : [DETAILS_TAB, CHAT_TAB, ACTIVITY_TAB]
  const { t } = useTranslation()
  const [internalTab, setInternalTab] = useState<HistoryTab>(
    HISTORY_TAB.details,
  )
  const activeTab = value ?? internalTab

  const setTab = (next: HistoryTab) => {
    if (value === undefined) setInternalTab(next)
    onValueChange?.(next)
  }

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const tablistRef = useRef<HTMLDivElement | null>(null)
  const [indicator, setIndicator] = useState<IndicatorStyle | null>(null)

  const updateIndicator = useCallback(() => {
    const el = tabRefs.current[activeTab]
    const container = tablistRef.current
    if (!el || !container) return
    const elRect = el.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    setIndicator({
      left: elRect.left - containerRect.left + container.scrollLeft,
      width: elRect.width,
    })
  }, [activeTab])

  useLayoutEffect(() => {
    updateIndicator()
  }, [updateIndicator, activityCount, chatCount, loadboardPosted])

  useEffect(() => {
    const handler = () => updateIndicator()
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [updateIndicator])

  return (
    <div
      className={cn(
        'flex items-end border-b border-gray-200 bg-white px-4 pt-3.5',
        className,
      )}
    >
      <div
        ref={tablistRef}
        role="tablist"
        className="relative flex h-5 items-center"
      >
        {tabs.map((tab) => (
          <HistoryTabButton
            key={tab.value}
            label={t(tab.labelKey)}
            active={activeTab === tab.value}
            count={tabCount(tab.value, activityCount, chatCount)}
            showDot={tab.value === HISTORY_TAB.loadboard && loadboardPosted}
            onClick={() => setTab(tab.value)}
            innerRef={(el) => {
              tabRefs.current[tab.value] = el
            }}
          />
        ))}
        {indicator && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-px h-0.5 rounded-full bg-gray-900 transition-[left,width] duration-300 ease-out"
            style={{ left: indicator.left, width: indicator.width }}
          />
        )}
      </div>
    </div>
  )
}
